import { useState } from 'react'

import { Field, FieldDescription, FieldError, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Cap, Demo, DsSection } from '../primitives'

/** One labeled state in the states grid. */
function StateCell({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-3">
      <Cap>{label}</Cap>
      {children}
    </div>
  )
}

/**
 * Form field: a labeled text input composed from the Field primitives
 * (FieldLabel · Input · FieldDescription · FieldError). Rendered live from the
 * real components so the states below reflect the actual styling.
 */
export function FieldsSection() {
  const [value, setValue] = useState('')

  return (
    <DsSection id="fields" title="Form field">
      <Demo label="Anatomy — label · input · description">
        <Field className="max-w-sm">
          <FieldLabel htmlFor="fld-email">Email</FieldLabel>
          <Input id="fld-email" type="email" placeholder="you@company.com" />
          <FieldDescription>We&apos;ll only use this to send your COI.</FieldDescription>
        </Field>
      </Demo>

      <div>
        <Cap>States</Cap>
        <div className="mt-3 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
          <StateCell label="Default">
            <Field>
              <FieldLabel htmlFor="s-default">DOT number</FieldLabel>
              <Input id="s-default" placeholder="Your USDOT #" />
            </Field>
          </StateCell>

          <StateCell label="Filled">
            <Field>
              <FieldLabel htmlFor="s-filled">DOT number</FieldLabel>
              <Input id="s-filled" defaultValue="3300318" />
            </Field>
          </StateCell>

          <StateCell label="Focus / typing — click in">
            <Field>
              <FieldLabel htmlFor="s-focus">DOT number</FieldLabel>
              <Input
                id="s-focus"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="Type here"
              />
            </Field>
          </StateCell>

          <StateCell label="With description">
            <Field>
              <FieldLabel htmlFor="s-desc">DOT number</FieldLabel>
              <Input id="s-desc" placeholder="Your USDOT #" />
              <FieldDescription>No phone or email needed.</FieldDescription>
            </Field>
          </StateCell>

          {/* Required is the unmarked default, so this is the only marked state. */}
          <StateCell label="Optional — FieldLabel optional">
            <Field>
              <FieldLabel htmlFor="s-optional" optional>
                DOT number
              </FieldLabel>
              <Input id="s-optional" placeholder="Your USDOT #" />
            </Field>
          </StateCell>

          <StateCell label="Disabled">
            <Field data-disabled="true">
              <FieldLabel htmlFor="s-disabled">DOT number</FieldLabel>
              <Input id="s-disabled" placeholder="Your USDOT #" disabled />
            </Field>
          </StateCell>

          <StateCell label="Invalid / error">
            <Field data-invalid="true">
              <FieldLabel htmlFor="s-error">DOT number</FieldLabel>
              <Input id="s-error" defaultValue="abc" aria-invalid />
              <FieldError>Enter your 1–8 digit DOT number.</FieldError>
            </Field>
          </StateCell>
        </div>
      </div>

      <div>
        <Cap>Sizes — sm · default · lg</Cap>
        <div className="mt-3 flex max-w-sm flex-col gap-4">
          <Input size="sm" placeholder="Small — 32px" />
          <Input placeholder="Default — 40px" />
          <Input size="lg" placeholder="Large — 50px" />
        </div>
      </div>
    </DsSection>
  )
}
